/**
 * Matrix Room Utilities
 * Reusable utility functions for Matrix room operations
 */

import { captureChatException } from 'shared/sentry';
import { formatTimeAgo } from '../function';
import { DEFAULT_USER_AVATAR } from '../../constants/common';

// Constants
export const DEFAULT_AVATAR = DEFAULT_USER_AVATAR;
export const AVATAR_SIZE = 50;
export const SYNC_STATE_PREPARED = 'PREPARED';
export const SYNC_STATE_SYNCING = 'SYNCING';
export const SYNC_STATE_CATCHUP = 'CATCHUP';
export const MESSAGE_TYPE = 'm.room.message';
/** Custom room event for call/session signals (not rendered as chat messages). */
export const JUICYHOUSE_CALL_EVENT = 'com.juicyhouse.call_event';
export const ENCRYPTED_EVENT_TYPE = 'm.room.encrypted';
export const MIN_MEMBERS_FOR_ROOM = 2;
const MAX_PREVIEW_TIMELINE_SCAN = 30;
const MAX_UNREAD_TIMELINE_SCAN = 40;

/**
 * Get the effective event type for message rendering.
 * For encrypted events, Matrix SDK exposes decrypted type via getClearType().
 * @param {Object} event - Matrix event
 * @returns {string|null} Effective (decrypted) event type
 */
export const getEffectiveEventType = (event) => {
  if (!event) return null;

  const wireType = event.getType?.() || null;
  if (!wireType) return null;

  // If the SDK version exposes getClearType, prefer it.
  try {
    const clearType = event.getClearType?.();
    if (clearType) return clearType;
  } catch {
    // ignore
  }

  // Many SDK versions keep encrypted events as m.room.encrypted on the wire.
  // In that case, infer "message" when decrypted clear content looks like a message.
  if (wireType === ENCRYPTED_EVENT_TYPE) {
    const clearContent = event.getClearContent?.();
    if (clearContent?.msgtype && (typeof clearContent?.body === 'string' || typeof clearContent?.formatted_body === 'string')) {
      return MESSAGE_TYPE;
    }
  }

  return wireType;
};

/**
 * Get the effective event content for message rendering.
 * For encrypted events, Matrix SDK exposes decrypted content via getClearContent().
 * @param {Object} event - Matrix event
 * @returns {Object|null} Effective (decrypted) event content
 */
export const getEffectiveEventContent = (event) => {
  if (!event) return null;

  const wireType = event.getType?.();
  if (wireType === ENCRYPTED_EVENT_TYPE) {
    // For encrypted events: if/when decrypted, getClearContent() is what we want.
    return event.getClearContent?.() || event.getContent?.() || null;
  }

  return event.getContent?.() || null;
};

/**
 * Determine if an event represents a room message (including decrypted encrypted events).
 * @param {Object} event - Matrix event
 * @returns {boolean}
 */
export const isRoomMessageEvent = (event) => getEffectiveEventType(event) === MESSAGE_TYPE;

/**
 * Extract message body from an event if it is a message event.
 * @param {Object} event - Matrix event
 * @returns {string|null} Message body
 */
export const getRoomMessageBody = (event) => {
  if (!isRoomMessageEvent(event)) return null;
  const content = getEffectiveEventContent(event);
  const body = content?.body;
  if (typeof body !== 'string') return null;
  const trimmed = body.trim();
  return trimmed ? trimmed : null;
};

/** Relation type for message edits (Matrix spec). */
const REL_TYPE_REPLACE = 'm.replace';
const NEW_CONTENT_KEY = 'm.new_content';
const RELATES_TO_KEY = 'm.relates_to';

/**
 * Whether this event is an edit (m.replace) event. Such events should not be shown as standalone messages.
 * @param {Object} event - Matrix event
 * @returns {boolean}
 */
export const isReplacementEvent = (event) => {
  if (!event) return false;
  const content = getEffectiveEventContent(event);
  const rel = content?.[RELATES_TO_KEY];
  return rel?.rel_type === REL_TYPE_REPLACE && typeof rel?.event_id === 'string';
};

/**
 * Event ID that this replacement event targets.
 * @param {Object} event - Matrix event (should be a replacement event)
 * @returns {string|null}
 */
export const getReplacementTargetEventId = (event) => {
  if (!event) return null;
  const content = getEffectiveEventContent(event);
  return content?.[RELATES_TO_KEY]?.event_id ?? null;
};

/**
 * Strip Matrix edit fallback prefix from body if present.
 * Edit events use body like "* { \"type\": \"media\", ... }" as fallback; we want the JSON part for display.
 * @param {string} body - Message body (may start with "* " followed by JSON)
 * @returns {string} Body with "* " prefix removed when remainder looks like JSON, otherwise unchanged
 */
export const stripMatrixEditFallbackPrefix = (body) => {
  if (typeof body !== 'string') return body;
  const trimmed = body.trim();
  if (trimmed.startsWith('* ')) {
    const rest = trimmed.slice(2).trim();
    if (rest.startsWith('{')) return rest;
  }
  return trimmed || body;
};

const replacementBodyFromEvent = (event) => {
  const content = getEffectiveEventContent(event);
  const newContent = content?.[NEW_CONTENT_KEY];
  let newBody = newContent?.body;
  if (typeof newBody !== 'string') {
    newBody = content?.body;
  }
  if (typeof newBody !== 'string') return null;
  const normalized = stripMatrixEditFallbackPrefix(newBody.trim());
  return normalized || null;
};

/**
 * Latest m.replace body per original event id. Built once per timeline pass (O(n)).
 * @param {Array<Object>} timelineEvents
 * @returns {Map<string, string>}
 */
export const buildReplacementBodyByEventId = (timelineEvents) => {
  const latestByTarget = new Map();
  const bodies = new Map();
  if (!Array.isArray(timelineEvents) || timelineEvents.length === 0) return bodies;

  for (let i = 0; i < timelineEvents.length; i += 1) {
    const ev = timelineEvents[i];
    if (!isReplacementEvent(ev)) continue;
    const targetId = getReplacementTargetEventId(ev);
    if (!targetId) continue;
    const ts = ev.getTs?.() ?? 0;
    const prevTs = latestByTarget.get(targetId);
    if (typeof prevTs === 'number' && ts < prevTs) continue;
    const body = replacementBodyFromEvent(ev);
    if (!body) continue;
    latestByTarget.set(targetId, ts);
    bodies.set(targetId, body);
  }

  return bodies;
};

/**
 * Get the body to display for a message event, applying the latest edit (m.replace) if any.
 * Used so that after editing (e.g. lock_content: true -> false), the UI and refresh show the updated content.
 * @param {Object} event - Matrix message event
 * @param {Array<Object>} timelineEvents - All message events in the room timeline (same order as room)
 * @returns {string|null} Effective body (from m.new_content if edited, else event's body)
 */
export const getEffectiveMessageBodyForEvent = (event, timelineEvents = []) => {
  if (!event || !isRoomMessageEvent(event)) return null;
  const eventId = event.getId?.() ?? null;
  if (!eventId) return stripMatrixEditFallbackPrefix(getRoomMessageBody(event));

  let latestReplacement = null;
  let latestTs = 0;
  for (let i = 0; i < timelineEvents.length; i++) {
    const ev = timelineEvents[i];
    if (!ev || getReplacementTargetEventId(ev) !== eventId) continue;
    const ts = ev.getTs?.() ?? 0;
    if (ts >= latestTs) {
      latestTs = ts;
      latestReplacement = ev;
    }
  }

  if (latestReplacement) {
    const normalized = replacementBodyFromEvent(latestReplacement);
    if (normalized) return normalized;
  }

  return stripMatrixEditFallbackPrefix(getRoomMessageBody(event));
};

/**
 * Whether a timeline event should trigger a "last message / unread" refresh.
 * Includes encrypted events because the SDK can emit timeline updates before the
 * message is decrypted, and later emits `Event.decrypted` for the same event.
 * @param {Object} event - Matrix event
 * @returns {boolean}
 */
export const shouldRefreshRoomsForTimelineEvent = (event) => {
  try {
    if (!event) return false;
    // Edits (m.replace) are not new messages: refresh roomlist entry incrementally instead.
    if (isReplacementEvent(event)) return false;
    const wireType = event.getType?.();
    const effectiveType = getEffectiveEventType(event);
    return (
      wireType === ENCRYPTED_EVENT_TYPE ||
      effectiveType === MESSAGE_TYPE ||
      wireType === 'm.room.redaction' ||
      effectiveType === 'm.room.redaction'
    );
  } catch {
    return false;
  }
};

/**
 * Get the last message preview from a room using Matrix SDK best practices
 * Uses room.getLastLiveEvent() for efficiency, falls back to timeline scanning
 * @param {Object} room - Matrix room object
 * @returns {Object|null} Last message preview with body, sender, and timestamp
 */
export const getLastMessagePreview = (room) => {
  if (!room) return null;

  try {
    // Use Matrix SDK best practice: getLastLiveEvent() for the most recent event
    const lastEvent = room.getLastLiveEvent();

    if (lastEvent) {
      const messageBody = getRoomMessageBody(lastEvent);
      if (messageBody) {
        return {
          event: lastEvent,
          body: messageBody,
          sender: lastEvent.getSender?.(),
          timestamp: lastEvent.getTs?.(),
        };
      }

      // If the last live event is an encrypted message (not yet decrypted),
      // still return a stable preview so lists reorder immediately.
      const wireType = lastEvent.getType?.();
      if (wireType === ENCRYPTED_EVENT_TYPE) {
        return {
          event: lastEvent,
          body: 'New message',
          sender: lastEvent.getSender?.(),
          timestamp: lastEvent.getTs?.(),
        };
      }
    }

    // Fallback: scan backwards through timeline for a message event
    const rawTimeline =
      room.getLiveTimeline?.().getEvents?.() ||
      room.timeline ||
      [];

    const timeline = Array.isArray(rawTimeline)
      ? rawTimeline.slice(-MAX_PREVIEW_TIMELINE_SCAN)
      : [];

    for (let i = timeline.length - 1; i >= 0; i--) {
      const event = timeline[i];
      if (!event) continue;

      const messageBody = getRoomMessageBody(event);
      if (messageBody) {
        return {
          event,
          body: messageBody,
          sender: event.getSender?.(),
          timestamp: event.getTs?.(),
        };
      }

      const wireType = event.getType?.();
      if (wireType === ENCRYPTED_EVENT_TYPE) {
        return {
          event,
          body: 'New message',
          sender: event.getSender?.(),
          timestamp: event.getTs?.(),
        };
      }
    }
  } catch (error) {
    // Silently handle errors to prevent UI crashes
    captureChatException(error, { tags: { feature: 'matrix-rooms', action: 'last-message-preview' } });
    return null;
  }

  return null;
};

/**
 * Compute unread *message* count for a room for the current user.
 *
 * Do not use Matrix's `unread_notifications.notification_count` (SDK:
 * `room.getUnreadNotificationCount`). That is a push-notification tally and can
 * include non-message noise, so badges can show higher than visible chat bubbles
 * (e.g. 3 when only 2 text messages were sent). Date separators are UI-only and
 * are never Matrix events — they must not affect this count.
 *
 * Counts incoming `m.room.message` events after the user's read receipt (m.read)
 * in the currently-loaded live timeline window, matching what the chat UI shows.
 *
 * @param {Object} room - Matrix room instance
 * @param {string} myUserId - Current user's Matrix user id (mxid)
 * @returns {number} unread message count (>= 0)
 */
export const getRoomUnreadMessageCount = (room, myUserId) => {
  if (!room || !myUserId) return 0;

  try {
    const rawTimeline =
      room.getLiveTimeline?.().getEvents?.() ||
      room.timeline ||
      [];

    const timeline = Array.isArray(rawTimeline)
      ? rawTimeline.slice(-MAX_UNREAD_TIMELINE_SCAN)
      : [];

    if (timeline.length === 0) return 0;

    // Read receipt boundary: last event ID we've read up to.
    const readUpToEventId =
      room.getEventReadUpTo?.(myUserId, false) ||
      room.getEventReadUpTo?.(myUserId) ||
      null;

    let startIndex = 0;
    if (readUpToEventId) {
      const idx = timeline.findIndex((ev) => ev?.getId?.() === readUpToEventId);
      startIndex = idx >= 0 ? idx + 1 : 0;
    }

    let count = 0;
    for (let i = startIndex; i < timeline.length; i += 1) {
      const ev = timeline[i];
      if (!ev) continue;
      if (isReplacementEvent(ev)) continue;
      if (!isRoomMessageEvent(ev)) continue;
      // Align with chat UI: empty-body / non-renderable events are not shown as bubbles.
      if (!getRoomMessageBody(ev)) continue;
      const sender = ev.getSender?.();
      if (!sender || sender === myUserId) continue; // only count incoming messages
      count += 1;
    }

    return count;
  } catch (e) {
    captureChatException(e, { tags: { feature: 'matrix-rooms', action: 'unread-count' } });
    return 0;
  }
};

/**
 * True when a string looks like an app-internal id (e.g. Mongo ObjectId) wrongly used as a display name.
 */
export const looksLikeOpaqueTechnicalId = (str) => {
  if (!str || typeof str !== 'string') return false;
  const t = str.trim();
  if (t.length < 12) return false;
  const hexRuns = t.match(/[0-9a-f]{16,}/gi) || [];
  if (hexRuns.some((run) => run.length >= 20)) return true;
  const compact = t.replace(/[^0-9a-f]/gi, '');
  return compact.length >= 20 && compact.length / Math.max(t.length, 1) >= 0.82;
};

/**
 * Matrix SDK default name when every other member has left (account deleted / left).
 * Matches `matrix-js-sdk` `RoomNameType.EmptyRoom` in `Room.roomNameGenerator`:
 * - "Empty room"
 * - "Empty room (was {oldName})"
 *
 * CS API (room names §): when `joined + invited <= 1`, clients should show an empty-room
 * form derived from heroes / former members.
 * @see https://spec.matrix.org/v1.14/client-server-api/#calculating-the-display-name-for-a-room
 * @param {unknown} name
 * @returns {boolean}
 */
export const isAbandonedDmRoomName = (name) => {
  if (!name || typeof name !== 'string') return false;
  // Exact SDK formats only — avoid matching custom m.room.name values like "Empty room tour".
  return /^empty room(?:\s*\(was .+\))?$/i.test(name.trim());
};

/**
 * Other DM participant from /sync room summary `m.heroes` (lazy_load_members).
 * Spec: heroes are used to generate a room name when `m.room.name` / canonical alias
 * are unset. When no joined/invited members remain, servers may still list left/banned
 * users as heroes so clients can show "Empty room (was …)".
 * Spec sends MXID strings; matrix-js-sdk `Room.setSummary` maps them to `{ userId }`
 * and already drops self.
 * @param {Object} room
 * @param {string} myUserId
 * @returns {string|null}
 */
const getHeroOtherUserId = (room, myUserId) => {
  if (!Array.isArray(room?.heroes)) return null;
  for (let i = 0; i < room.heroes.length; i += 1) {
    const hero = room.heroes[i];
    const userId = typeof hero === 'string' ? hero : hero?.userId;
    if (userId && userId !== myUserId) return userId;
  }
  return null;
};

/**
 * Joined + invited member count (CS API alone-check uses both).
 * Prefers matrix-js-sdk `Room.getInvitedAndJoinedMemberCount()` when present.
 * @param {Object} room
 * @returns {number}
 */
const getActiveMemberCount = (room) => {
  if (typeof room?.getInvitedAndJoinedMemberCount === 'function') {
    const total = room.getInvitedAndJoinedMemberCount();
    return Number.isFinite(total) ? Math.max(total, 0) : 0;
  }
  const members = room?.getJoinedMembers?.() || [];
  const joined =
    typeof room?.getJoinedMemberCount === 'function'
      ? room.getJoinedMemberCount()
      : members.length;
  const invited =
    typeof room?.getInvitedMemberCount === 'function'
      ? room.getInvitedMemberCount()
      : 0;
  const joinedN = Number.isFinite(joined) ? joined : 0;
  const invitedN = Number.isFinite(invited) ? invited : 0;
  return Math.max(joinedN + invitedN, 0);
};

/**
 * Human-readable label for a room member (VoIP, headers). Strips "User " prefix and avoids raw internal ids.
 * @param {Object|null|undefined} member - Matrix RoomMember
 * @returns {string|null}
 */
export const memberDisplayNameForUi = (member) => {
  if (!member) return null;
  let d = member.name ?? member.rawDisplayName;
  if (typeof d === 'string' && d.startsWith('User ')) {
    d = d.substring(5).trim();
  }
  if (typeof d !== 'string') d = '';
  if (looksLikeOpaqueTechnicalId(d)) {
    const lp = member.userId?.split(':')?.[0]?.replace(/^@/, '');
    if (lp && !looksLikeOpaqueTechnicalId(lp)) return lp;
  }
  if (d) return d;
  return member.userId?.split(':')[0]?.replace('@', '') || null;
};

/**
 * Display name from the SDK (uses m.heroes when members are lazy-loaded).
 * @param {Object} room
 * @param {string} myUserId
 * @returns {string|null}
 */
const getSdkRoomDisplayName = (room, myUserId) => {
  // Room.name is calculated by the SDK from m.room.name and /sync m.heroes.
  if (typeof room?.name === 'string' && room.name.trim()) {
    return room.name.trim();
  }
  if (typeof room?.getDefaultRoomName === 'function') {
    const implicit = room.getDefaultRoomName(myUserId);
    if (implicit && typeof implicit === 'string' && implicit.trim()) {
      return implicit.trim();
    }
  }
  return null;
};

/**
 * True when a 1:1 Matrix room should be omitted from the inbox because the peer
 * left or deleted their account.
 *
 * Decision order (CS API room-name algorithm + matrix-js-sdk `calculateRoomName`):
 * 1. SDK name is EmptyRoom sentinel → abandoned (heroes may still list the leaver).
 * 2. `joined + invited` ≥ 2 → active DM / pending invite (not alone).
 * 3. Alone with no heroes → not listable.
 * 4. Alone with heroes but not EmptyRoom → treat as lazy_load / rename race; keep
 *    until sync settles on EmptyRoom (or peer reappears).
 *
 * @param {Object} room - Matrix room
 * @param {string} myUserId
 * @returns {boolean}
 */
export const isAbandonedDmRoom = (room, myUserId) => {
  if (!room || !myUserId) return true;

  const sdkName = getSdkRoomDisplayName(room, myUserId);
  if (isAbandonedDmRoomName(sdkName)) return true;

  // Spec: alone when joined_member_count + invited_member_count <= 1
  if (getActiveMemberCount(room) >= MIN_MEMBERS_FOR_ROOM) return false;

  // Alone. Heroes without EmptyRoom can be a transient lazy-load / rename gap.
  return !getHeroOtherUserId(room, myUserId);
};

/**
 * Filter inbox/UI rows whose display name is the SDK EmptyRoom sentinel.
 * Use on already-converted room list objects (`username` field).
 * @param {Array|null|undefined} rooms
 * @returns {Array}
 */
export const filterAbandonedInboxRooms = (rooms) => {
  if (!Array.isArray(rooms)) return [];
  return rooms.filter((room) => room && !isAbandonedDmRoomName(room?.username));
};

/**
 * Drop cached inbox rows for DMs where the peer left / deleted their account.
 * Prevents "@empty room (was …)" from sticking after membership updates.
 * @param {Array|null|undefined} cached
 * @param {Object|null|undefined} client - Matrix client
 * @param {string|null|undefined} myUserId
 * @returns {Array}
 */
export const pruneAbandonedInboxEntries = (cached, client, myUserId) => {
  if (!Array.isArray(cached) || !cached.length) return Array.isArray(cached) ? cached : [];
  if (!client || !myUserId) return cached;

  return cached.filter((entry) => {
    if (isAbandonedDmRoomName(entry?.username)) return false;
    const id = entry?.roomId || entry?._id;
    if (!id) return false;
    const room = client.getRoom?.(id);
    // Room not in the client yet (early sync) — keep cache until we can evaluate.
    if (!room) return true;
    return !isAbandonedDmRoom(room, myUserId);
  });
};

/**
 * Get the display name for a room
 * @param {Object} room - Matrix room object
 * @param {Array} members - Array of room members
 * @param {string} myUserId - Current user's Matrix user ID
 * @returns {string} Display name for the room
 */
export const getRoomDisplayName = (room, members, myUserId) => {
  const joined = Array.isArray(members) ? members : [];
  const otherMember = joined.find((m) => m.userId !== myUserId);

  if (otherMember && joined.length <= MIN_MEMBERS_FOR_ROOM) {
    return (
      memberDisplayNameForUi(otherMember) ||
      otherMember.userId?.split(':')[0]?.replace('@', '') ||
      getSdkRoomDisplayName(room, myUserId) ||
      'Unknown'
    );
  }

  if (joined.length > MIN_MEMBERS_FOR_ROOM) {
    if (room.name && room.name.startsWith('User ')) {
      return room.name.substring(5); // Remove "User " prefix (5 characters)
    }
    return room.name || `Group (${joined.length - 1} others)`;
  }

  // lazyLoadMembers: getJoinedMembers() is often only self until OOB load.
  // Room.name / getDefaultRoomName already use /sync m.heroes.
  return getSdkRoomDisplayName(room, myUserId) || 'Unknown';
};

/**
 * Get avatar URL for a room
 * @param {Object} room - Matrix room object
 * @param {Array} members - Array of room members
 * @param {Object} client - Matrix client instance
 * @param {string} myUserId - Current user's Matrix user ID
 * @returns {string} Avatar URL
 */
export const getRoomAvatar = (room, members, client, myUserId) => {
  if (!room || !members || !client) {
    return DEFAULT_AVATAR;
  }

  try {
    // One-to-one chat: use other member's avatar
    if (members.length === MIN_MEMBERS_FOR_ROOM) {
      const otherMember = members.find(m => m.userId !== myUserId);
      if (otherMember) {
        const avatarUrl = otherMember.getAvatarUrl?.(
          client.baseUrl,
          AVATAR_SIZE,
          AVATAR_SIZE,
          'scale',
          true
        );
        return avatarUrl || DEFAULT_AVATAR;
      }
    }

    // Group chat: use room avatar
    const roomAvatarUrl = room.getAvatarUrl?.(
      client.baseUrl,
      AVATAR_SIZE,
      AVATAR_SIZE,
      'scale',
      true
    );
    return roomAvatarUrl || DEFAULT_AVATAR;
  } catch (error) {
    captureChatException(error, { tags: { feature: 'matrix-rooms', action: 'room-avatar' } });
    return DEFAULT_AVATAR;
  }
};

/**
 * Convert Matrix room to user list format
 * @param {Object} room - Matrix room object
 * @param {Object} client - Matrix client instance
 * @param {string} myUserId - Current user's Matrix user ID
 * @returns {Object|null} Room data in user list format
 */
export const convertRoomToUserFormat = (room, client, myUserId) => {
  if (!room || !client || !myUserId) {
    return null;
  }

  try {
    // Peer left / account deleted → SDK EmptyRoom name (heroes may still list them).
    if (isAbandonedDmRoom(room, myUserId)) {
      return null;
    }

    const members = room.getJoinedMembers?.() || [];
    // lazy_load_members: getJoinedMembers() is incomplete. Use summary counts + m.heroes.
    const heroOtherUserId = getHeroOtherUserId(room, myUserId);
    const otherMember = members.find((m) => m.userId !== myUserId);
    const otherUserId = otherMember?.userId || heroOtherUserId;

    const displayName = getRoomDisplayName(room, members, myUserId);

    // Get last message preview using Matrix SDK best practices
    const lastMessagePreview = getLastMessagePreview(room);
    const lastMessage = lastMessagePreview?.body || 'No messages yet';
    const lastTimestamp = lastMessagePreview?.timestamp || 0;
    const lastTime = lastTimestamp ? formatTimeAgo(lastTimestamp) : '';

    // Only show unread for *received* messages (after read receipt). Do not use
    // Matrix notification count here, so sent messages / creator→subscriber messages
    // never show the pink dot; only unread received messages do, until the user opens the chat.
    const unreadMessageCount = getRoomUnreadMessageCount(room, myUserId);
    const unreadCount = Math.max(unreadMessageCount, 0);

    return {
      _id: room.roomId,
      roomId: room.roomId,
      matrixUserId: otherUserId,
      // App routes (`/my/messages/chat/:userId`) expect the backend user `_id`, not a Matrix
      // display name. That id is merged in from `/matrix/rooms` in the messages list screens.
      userId: null,
      username: displayName,
      message: lastMessage,
      time: lastTime,
      msg_count: unreadCount > 0 ? unreadCount : null,
      status: null,
      badge: null,
      daysRemaining: null,
      priority: null,
      lastTimestamp, // Store for sorting
      profilePicture: null,
      // Last message sender (Matrix mxid) — used to infer creator has replied when backend flag is absent
      lastMessageSenderId: lastMessagePreview?.sender ?? null,
    };
  } catch (error) {
    captureChatException(error, { tags: { feature: 'matrix-rooms', action: 'convert-room' } });
    return null;
  }
};

/**
 * Sort rooms by most recent message timestamp
 * @param {Array} rooms - Array of room data objects
 * @returns {Array} Sorted array of rooms
 */
export const sortRoomsByTimestamp = (rooms) => {
  if (!Array.isArray(rooms)) {
    return [];
  }

  return [...rooms].sort((a, b) => {
    const timestampA = a.lastTimestamp || 0;
    const timestampB = b.lastTimestamp || 0;
    return timestampB - timestampA;
  });
};

/**
 * Generate stable ring class based on roomId hash for avatar styling
 * Ensures consistent colors per conversation regardless of sort order
 * @param {string} roomId - Matrix room ID
 * @returns {string} CSS class name
 */
export const getRingClass = (roomId) => {
  if (!roomId || typeof roomId !== 'string') return 'cm_ring_1';

  // Hash roomId to get stable number between 1-8
  let hash = 0;
  for (let i = 0; i < roomId.length; i++) {
    hash = ((hash << 5) - hash) + roomId.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return `cm_ring_${(Math.abs(hash) % 8) + 1}`;
};
